import { MeteringPoint } from "models/electricity/metering-point";
import { ContractPeriodType } from "models/electricity/new-electricity-agreement";

export interface SimplifiedChargingAgreement {
  id: number;
  name: string;
  startDate: string;
}

export interface ChargingAgreement {
  id: number;
  contractNumber: string;
  customer: ChargingAgreementCustomer;
  contactPerson: ChargingAgreementContactPerson;
  startDate?: string;
  endDate?: string;
  offerDeadline?: string;
  status: ChargingAgreementStatus;
  contractPeriod: CharingAgreementContractPeriod;
  address?: string;
  city?: string;
  zip?: string;
  advancePaymentEuros?: number;
  hasEmergencyMaintenanceService: boolean;
  hasChargerMaintenanceService: boolean;
  chargerMaintenanceType?: ChargingBillingCycle;
  rfidPaymentCardCount?: number;
  simCardCount?: number;
  simCardPaymentType?: ChargingBillingCycle;
  meteringPoint: MeteringPoint;
  externalChargingStationId: string;
  purchaseOption: ChargingPurchaseOption;
  serviceOption: ChargingServiceOption;
  thirdPartyCompensationOption: ChargingThirdPartyCompensationOption;
  termsAndConditions: CharingTermsAndConditions;
  chargingStations: ChargingStation[];
  pricing: ChargingAgreementPricing;
  dateCreated: string;
  dateModified?: string;
}

export interface CharingAgreementContractPeriod {
  id: number;
  type: ContractPeriodType,
  name: string;
}

export interface CharingTermsAndConditions {
  id: number;
  confirmationText: string;
}

export interface ChargingAgreementCustomer {
  id: number;
  name: string;
  registryNumber: string;
  address?: string;
  zip?: string;
  city?: string;
  county?: string;
  eic?: string;
}

export interface ChargingAgreementContactPerson {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

export interface ChargingAgreementPricing {
  installationFeeEuros?: number;
  devicesRentalMonthlyFeeEuros?: number;
  devicesFullServiceRentalMonthlyFeeEuros?: number;
  devicesCostEuros?: number;
  thirdPartyCompensationNetoFeeEuros?: number;
  maintenanceMonthlyFeeEuros?: number;
  rfidPaymentCardMonthlyFeeEuros?: number;
  simCardMonthlyFeeEuros?: number;
  mediationAndBillingMonthlyFeePerNozzleEuros?: number;
  infrastructureUsageOneTimeFeePerNozzleEuros?: number;
  infrastructureUsageMonthlyFeePerNozzleEuros?: number;
  chargingServiceMonthlyFeeEuros?: number;
  thirdPartyChargingMarginalCentsKwh?: number;
  gridDayTariffCentsKwh?: number;
  gridNightTariffCentsKwh?: number;
}

export interface ChargingStation {
  id: number;
  serialNumber: string;
  latitude?: string;
  longitude: string;
  installationDate?: string;
  installationExtraFeeEuros?: number;
  nozzleCount?: number;
  model: ChargingStationModel;
}

export enum ChargingAgreementStatus {
  ACTIVE = 'ACTIVE',
  ENDED = 'ENDED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED'
}

export enum ChargingPurchaseOptionType {
  PURCHASE = 'PURCHASE',
  RENT = 'RENT',
  FULL_SERVICE_RENT = 'FULL_SERVICE_RENT',
  EXISTING = 'EXISTING',
  SERVICE_PROVIDER = 'SERVICE_PROVIDER'
}

export interface ChargingPurchaseOption {
  id: number;
  name: string;
  description?: string;
  type: ChargingPurchaseOptionType
}

export enum ChargingServiceOptionType {
  PUBLIC = 'PUBLIC',
  SEMI_PUBLIC = 'SEMI_PUBLIC',
  PRIVATE_AUTHENTICATED = 'PRIVATE_AUTHENTICATED',
  PRIVATE_NON_AUTHENTICATED = 'PRIVATE_NON_AUTHENTICATED'
}

export interface ChargingServiceOption {
  id: number;
  name: string;
  description?: string;
  type: ChargingServiceOptionType
}

export enum ChargingThirdPartyCompensationOptionType {
  FIXED = 'FIXED',
  TARIFF = 'TARIFF',
  NETO = 'NETO'
}

export interface ChargingThirdPartyCompensationOption {
  id: number;
  name: string;
  description?: string;
  type: ChargingThirdPartyCompensationOptionType
}

export interface ChargingStationModel {
  id: number;
  name: string;
  description?: string;
  rentalMonthlyFeeEuros: number;
  priceEuros: number;
}

export enum ChargingBillingCycle {
  YEARLY = 'YEARLY',
  QUARTERLY = 'QUARTERLY',
  MONTHLY = 'MONTHLY'
}

export const CHARGING_BILLING_CYCLE_MAP = {
  [ChargingBillingCycle.YEARLY] : 'Kord aastas',
  [ChargingBillingCycle.QUARTERLY] : 'Kord kvartalis',
  [ChargingBillingCycle.MONTHLY] : 'Kord kuus'
};

export const CHARGING_AGREEMENT_STATUS_MAP: {[key in ChargingAgreementStatus]: string} = {
  ACTIVE: 'Kehtiv',
  ENDED: 'Lõppenud',
  PENDING: 'Kinnitamise ootel',
  REJECTED: 'Tagasi lükatud',
  CANCELED: 'Tühistatud'
};